import React, { useState } from 'react';

import { toast } from 'sonner';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';
import { LeftCard, RightCard } from '@sandtech-hub-monorepo/ui-kit';
import { ForgotPasswordDocument } from '@sandtech-hub-monorepo/ehub-client';

import { ResetPasswordForm } from './components/ResetPasswordForm';
import {
  forgotPasswordCTAQuestion,
  forgotPasswordCTAText,
  forgotPasswordLinkSent,
  forgotPasswordSubtitle,
  forgotPasswordTitle,
} from '../../../../stringsManager';
import { ConfirmResetPasswordForm } from './components/ConfirmResetPasswordForm';
import { linkErrorMessage } from '../linkError';

export interface ResetPasswordProps {
  loginRoute: string;
  forgotPasswordCover: string;
  isStaff?: boolean;
  /** The reset email links here with the code, so the page has no code input. */
  codeByLink?: boolean;
  /**
   * Optional cover chrome: a logo drawn over the cover image, anchored top
   * (default) or bottom, plus a dark gradient scrim for photos that don't
   * have enough natural contrast for a light logo.
   */
  coverLogo?: React.ReactNode;
  coverLogoPosition?: 'top' | 'bottom';
  overlay?: boolean;
  overlayClassName?: string;
  /**
   * Renders a compact solid-color banner carrying coverLogo, visible only
   * below `lg` (where the cover photo is otherwise hidden entirely).
   * Undefined (the default) renders nothing, matching every existing
   * caller's current mobile behavior.
   */
  coverMobileHeaderClassName?: string;
}

export const ResetPassword = ({
  isStaff,
  codeByLink,
  ...props
}: ResetPasswordProps) => {
  const navigate = useNavigate();

  const [isResetPwdCodeSent, setIsResetPwdCodeSent] = useState(false);
  const [email, setEmail] = useState('');
  // A failed reset link falls back to requesting a new one.
  const [linkError, setLinkError] = useState<string | null>(null);

  const { loading: confirmLoading, confirmResetPassword } = useCognito();
  const [params] = useSearchParams();
  const linkEmail = params.get('email');
  const linkCode = linkError ? null : params.get('code');
  // The link's one-time code has no business on the login page.
  const loginParams = new URLSearchParams(params);
  loginParams.delete('code');
  const loginSearch = loginParams.toString();
  const [forgotPassword, { loading: forgotPasswordLoading }] = useMutation(
    ForgotPasswordDocument
  );

  const handleResetPassword = (values: { email: string }) => {
    forgotPassword({
      variables: { input: { email: values.email, isStaff: isStaff ?? false } },
      onCompleted: () => {
        setIsResetPwdCodeSent(true);
        setEmail(values.email);
      },
    });
  };

  const handleConfirmResetPassword = async (values: {
    email: string;
    newPassword: string;
    confirmNewPassword: string;
    confirmationCode: string;
  }) => {
    const reset = await confirmResetPassword(
      {
        username: values.email,
        newPassword: values.newPassword,
        confirmationCode: values.confirmationCode,
      },
      linkCode
        ? (error) =>
            setLinkError(linkErrorMessage(error, 'Request a new one below.'))
        : undefined
    );
    if (!reset) return;

    toast.success('You have successfully reset your password!');
    navigate({ pathname: props.loginRoute, search: loginSearch });
  };

  return (
    <>
      <LeftCard
        backgroundImage={props.forgotPasswordCover}
        logo={props.coverLogo}
        logoPosition={props.coverLogoPosition}
        overlay={props.overlay}
        overlayClassName={props.overlayClassName}
        mobileHeaderClassName={props.coverMobileHeaderClassName}
      />
      <RightCard>
        <div className="flex flex-col space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {forgotPasswordTitle}
          </h1>
          {linkError && !isResetPwdCodeSent ? (
            <p className="text-sm text-destructive">{linkError}</p>
          ) : (
            !isResetPwdCodeSent &&
            !linkCode && (
              <p className="text-sm text-muted-foreground">
                {forgotPasswordSubtitle}
              </p>
            )
          )}
        </div>
        {linkEmail && linkCode ? (
          <ConfirmResetPasswordForm
            email={linkEmail}
            code={linkCode}
            onSubmit={handleConfirmResetPassword}
            loading={confirmLoading}
          />
        ) : isResetPwdCodeSent && codeByLink ? (
          <p className="text-sm text-muted-foreground">
            {forgotPasswordLinkSent}
          </p>
        ) : isResetPwdCodeSent ? (
          <ConfirmResetPasswordForm
            email={email}
            onSubmit={handleConfirmResetPassword}
            loading={confirmLoading}
          />
        ) : (
          <ResetPasswordForm
            onSubmit={handleResetPassword}
            loading={forgotPasswordLoading}
            defaultEmail={linkEmail ?? ''}
          />
        )}

        <div className="flex gap-1 justify-center leading-tight">
          <p className="text-sm text-muted-foreground">
            {forgotPasswordCTAQuestion}
          </p>
          <Link
            to={`${props.loginRoute}${loginSearch ? '?' + loginSearch : ''}`}
            className="text-sm text-primary"
          >
            {forgotPasswordCTAText}
          </Link>
        </div>
      </RightCard>
    </>
  );
};
